import { Icon } from './Icon';
import { timeOnly } from '../lib/format';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { step } from '../telemetry';
import type { Address, SendResult } from '../types';

export interface ComposeDraft {
  to?: string;
  cc?: string;
  subject?: string;
  text?: string;
  /** Cavab yazılırsa — zəncirin bağlanması üçün. */
  inReplyTo?: string;
  references?: string[];
  /**
   * Yönləndirmə mənbəyi — qoşmaları SERVER özü əlavə edir.
   *
   * ⚠️ Fayllar brauzerə endirilmir. Məktub onsuz da bizim IMAP-ımızdadır;
   * 20 MB-lıq qoşmanı endirib yenidən yükləmək eyni baytları şəbəkədən
   * iki dəfə keçirmək və mobil bağlantıda timeout deməkdir.
   */
  forwardOf?: { path: string; uid: number };
  /** Yönləndirilən qoşmaların adları — yazma pəncərəsində göstərilir. */
  forwardAttachments?: { filename: string; size: number }[];
  /**
   * Mövcud qaralamanın UID-i — REDAKTƏ rejimi.
   *
   * ⚠️ BUNSUZ HƏR AVTOSAXLAMA YENİ QARALAMA YARADIR.
   * Qaralamanı açıb bir hərf yazan istifadəçi Qaralamalar qovluğunda
   * eyni məktubun iki nüsxəsini görürdü. `replaceUid` verildikdə
   * `saveDraft` köhnəsini silib yenisini yazır.
   */
  replaceUid?: number;
}

interface Props {
  from: string;
  draft: ComposeDraft;
  onClose: () => void;
  /** Göndərildi — Sent qovluğu yenilənsin. */
  onSent: (r: SendResult) => void;
}

/**
 * Ünvan sətrini parçalayır: vergül və ya nöqtəli vergüllə ayrılmış siyahı.
 *
 * `Ad <ad@domen.az>` formatı da qəbul olunur, çünki istifadəçi çox vaxt
 * başqa poçt proqramından kopyalayır və orada ad da olur.
 */
function parseAddresses(raw: string): { list: Address[]; invalid: string[] } {
  const list: Address[] = [];
  const invalid: string[] = [];

  for (const part of raw.split(/[,;]/)) {
    const s = part.trim();
    if (!s) continue;

    const angled = /^(.*?)\s*<([^>]+)>$/.exec(s);
    const name = angled ? angled[1]!.trim().replace(/^"|"$/g, '') : '';
    const address = (angled ? angled[2]! : s).trim();

    // Sadə yoxlama — dəqiq RFC 5322 qaydası praktikada faydasızdır və
    // düzgün ünvanları rədd edir. Əsl yoxlamanı SMTP serveri edir.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
      invalid.push(s);
      continue;
    }
    list.push(name ? { name, address } : { name: '', address });
  }
  return { list, invalid };
}

/** Bayt sayını oxunaqlı ölçüyə çevirir. */
function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function messageFor(code: string, body: Record<string, unknown> = {}): string {
  switch (code) {
    case 'recipient_required':
      return 'Ən azı bir alıcı yazın.';
    case 'send_rate_limited':
      // ⚠️ Server bu halda hazır cümlə göndərir (saatlıq/günlük fərqi ilə).
      // Onu təkrar yazmırıq — məlumat serverdədir, təkrar yazsaq ikisi
      // vaxtla bir-birindən uzaqlaşar.
      return typeof body['reason'] === 'string'
        ? (body['reason'] as string)
        : 'Göndəriş limitiniz doldu. Məktub bir az sonra avtomatik göndəriləcək.';
    // ⚠️ ÖDƏNİŞ HALLARI AYRICA — «SMTP cavab vermədi» YAZMAQ OLMAZ.
    // Bu, texniki nasazlıq deyil və təkrar cəhd heç nə dəyişmir.
    // Səbəbi düz demək müştərini dəstəyə yazmaqdan xilas edir.
    case 'payment_required':
    case 'account_suspended':
      return typeof body['reason'] === 'string'
        ? (body['reason'] as string)
        : 'Hesabınızın vəziyyətinə görə göndəriş mümkün deyil.';
    case 'send_failed':
      return 'Göndərmə alınmadı. SMTP serveri cavab vermədi — bir azdan yenidən yoxlayın.';
    case 'no_mailbox':
      return 'Sizin poçt qutunuz yoxdur, ona görə göndərmək mümkün deyil.';
    default:
      return 'Məktub göndərilmədi. Bir azdan yenidən yoxlayın.';
  }
}

export function Compose({ from, draft, onClose, onSent }: Props): React.ReactElement {
  /**
   * Göndərə biləcəyimiz ünvanlar — qutunun özü + aktiv ləqəblər.
   *
   * ⚠️ SORĞU UĞURSUZ OLSA DA COMPOSE İŞLƏMƏLİDİR. Siyahı boş qalırsa
   * `from` işlədilir, yəni ən pis halda köhnə davranışa qayıdırıq —
   * məktub yazmaq heç vaxt bu əlavə sorğuya görə bloklanmamalıdır.
   */
  const [identities, setIdentities] = useState<string[]>([from]);
  const [sender, setSender] = useState(from);

  useEffect(() => {
    let cancelled = false;
    void api
      .identities()
      .then((r) => {
        if (!cancelled && r.addresses.length > 0) setIdentities(r.addresses);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const [to, setTo] = useState(draft.to ?? '');
  const [cc, setCc] = useState(draft.cc ?? '');
  const [bcc, setBcc] = useState('');
  const [showCc, setShowCc] = useState(Boolean(draft.cc));

  /**
   * Qoşmalar.
   *
   * ⚠️ FAYL BAZA64 KİMİ YADDAŞDA SAXLANILIR. Bu, kiçik və orta fayllar
   * üçün ən sadə yoldur, amma nəzərə alınmalıdır: base64 ölçünü ~33%
   * böyüdür, ona görə ümumi hədd faylların əsl ölçüsündən aşağı qoyulub.
   */
  const [files, setFiles] = useState<{ filename: string; content: string; contentType: string; size: number }[]>([]);
  const [attaching, setAttaching] = useState(false);

  const fileInput = useRef<HTMLInputElement>(null);

  /** Serverin gövdə limiti 40 MB-dır; base64 payını çıxıb 25 MB qoyuruq. */
  const MAX_TOTAL = 25 * 1024 * 1024;
  const totalSize = files.reduce((n, f) => n + f.size, 0);

  async function addFiles(list: FileList | null): Promise<void> {
    if (!list?.length) return;
    setAttaching(true);
    setError(null);
    try {
      const next = [...files];
      let sum = totalSize;
      for (const f of Array.from(list)) {
        if (sum + f.size > MAX_TOTAL) {
          setError(
            `Qoşmaların ümumi həcmi ${Math.round(MAX_TOTAL / 1024 / 1024)} MB-ı keçə bilməz. ` +
              `«${f.name}» əlavə edilmədi.`,
          );
          break;
        }
        // ⚠️ `readAsDataURL` nəticəsi `data:<tip>;base64,<məlumat>` şəklindədir —
        // yalnız vergüldən sonrakı hissə lazımdır.
        const b64 = await new Promise<string>((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result).split(',')[1] ?? '');
          r.onerror = () => rej(new Error('read'));
          r.readAsDataURL(f);
        });
        next.push({
          filename: f.name,
          content: b64,
          contentType: f.type || 'application/octet-stream',
          size: f.size,
        });
        sum += f.size;
      }
      setFiles(next);
    } catch {
      setError('Fayl oxuna bilmədi.');
    } finally {
      setAttaching(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }
  const [subject, setSubject] = useState(draft.subject ?? '');
  const [text, setText] = useState(draft.text ?? '');

  /**
   * İMZA.
   *
   * ⚠️ YALNIZ BİR DƏFƏ, PƏNCƏRƏ AÇILANDA əlavə olunur. Hər dəyişiklikdə
   * yenidən qoysaq, istifadəçinin sildiyi imza geri qayıdardı.
   *
   * ⚠️ CAVAB VƏ YÖNLƏNDİRMƏDƏ imza SİTATIN ÜSTÜNDƏ durur — mətn onsuz da
   * `\n\n` ilə başlayır, ona görə imza həmin boşluğa girir. Sitatın
   * altına qoysaq, uzun yazışmalarda heç kim onu görməzdi.
   */
  useEffect(() => {
    let cancelled = false;
    void api
      .signature()
      .then((r) => {
        if (cancelled || !r.signature) return;
        setText((prev) => {
          // Artıq imza varsa təkrar əlavə etmirik (məsələn qaralamadan
          // davam edilirsə).
          if (prev.includes(r.signature!)) return prev;
          return prev ? `${prev}\n\n${r.signature}` : `\n\n${r.signature}`;
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * QARALAMANIN AVTOMATİK SAXLANMASI.
   *
   * ⚠️ ƏVVƏL QARALAMA QOVLUĞU BOŞ QALIRDI. Panel `Qaralamalar` qovluğunu
   * göstərirdi, amma ora heç nə yazmırdı — pəncərə bağlananda mətn itirdi.
   * Uzun məktub yazıb səhvən bağlayan istifadəçi işini tamamilə itirirdi
   * və heç bir xəbərdarlıq yox idi.
   *
   * ⚠️ QOŞMALAR QARALAMAYA YAZILMIR. Onlar brauzerin yaddaşındadır və
   * hər saxlamada serverə göndərmək 25 MB-lıq faylı hər 5 saniyədə bir
   * yükləmək deməkdir. Bu, məqsədəuyğun deyil — istifadəçi xəbərdar edilir.
   */
  // ⚠️ BAŞLANĞIC DƏYƏR `draft.replaceUid`-dir. `null` ilə başlasaq,
  // mövcud qaralamanın redaktəsi ilk saxlamada DUBLİKAT yaradardı.
  const [draftUid, setDraftUid] = useState<number | null>(draft.replaceUid ?? null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  /** Göndərildikdən sonra qaralama saxlanmamalıdır — mesaj artıq yoldadır. */
  const sent = useRef(false);

  const saveDraft = async (): Promise<void> => {
    if (sent.current) return;
    // Boş qaralama saxlamırıq — istifadəçi pəncərəni açıb bağlayıbsa,
    // Qaralamalar qovluğunda boş sətir qalmamalıdır.
    if (!to.trim() && !subject.trim() && !text.trim()) return;
    setSavingDraft(true);
    try {
      const r = await api.saveDraft({
        to: parseAddresses(to).list,
        ...(cc.trim() ? { cc: parseAddresses(cc).list } : {}),
        ...(bcc.trim() ? { bcc: parseAddresses(bcc).list } : {}),
        subject,
        text,
        ...(draft.inReplyTo ? { inReplyTo: draft.inReplyTo } : {}),
        ...(draft.references?.length ? { references: draft.references } : {}),
        ...(draftUid !== null ? { replaceUid: draftUid } : {}),
      });
      setDraftUid(r.uid);
      setSavedAt(new Date());
    } catch {
      // Səssiz — avtomatik saxlama uğursuzluğu yazmağı kəsməməlidir.
      // İstifadəçi «saxlanıldı» yazısını görməyəcək və bu, kifayət qədər
      // siqnaldır.
    } finally {
      setSavingDraft(false);
    }
  };

  // Yazı dayandıqdan 3 saniyə sonra saxlanılır — hər hərfdə deyil.
  useEffect(() => {
    if (!to.trim() && !subject.trim() && !text.trim()) return;
    const t = setTimeout(() => void saveDraft(), 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, cc, bcc, subject, text]);

  /**
   * ⚠️ BRAUZER BAĞLANANDA XƏBƏRDARLIQ.
   * Avtomatik saxlama 3 saniyəlik gecikmə ilə işləyir; istifadəçi son
   * cümləni yazıb dərhal tabı bağlasa, həmin cümlə hələ serverdə deyil.
   */
  useEffect(() => {
    const onLeave = (e: BeforeUnloadEvent): void => {
      if (sent.current) return;
      if (!to.trim() && !subject.trim() && !text.trim()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [to, subject, text]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    const t = parseAddresses(to);
    const c = parseAddresses(cc);
    const b = parseAddresses(bcc);
    const invalid = [...t.invalid, ...c.invalid, ...b.invalid];

    if (invalid.length) {
      setError(`Bu ünvanlar düzgün deyil: ${invalid.join(', ')}`);
      return;
    }
    if (t.list.length + c.list.length + b.list.length === 0) {
      setError('Ən azı bir alıcı yazın.');
      return;
    }

    setBusy(true);
    try {
      step(`Məktub göndərilir: ${t.list.length + c.list.length + b.list.length} alıcı`);
      const r = await api.send({
        // ⚠️ Yalnız FƏRQLİ olanda göndərilir. Həmişə göndərsək, ləqəbi
        // olmayan hesablarda da server ünvanı yoxlamağa məcbur olardı.
        ...(sender && sender !== from ? { from: sender } : {}),
        to: t.list,
        ...(c.list.length ? { cc: c.list } : {}),
        ...(b.list.length ? { bcc: b.list } : {}),
        subject,
        ...(text ? { text } : {}),
        ...(draft.inReplyTo ? { inReplyTo: draft.inReplyTo } : {}),
        ...(draft.references?.length ? { references: draft.references } : {}),
        // ⚠️ YALNIZ GÖNDƏRİŞDƏ, qaralamada YOX. Qaralama saxlananda
        // qoşmaları da yığsaq, hər avtomatik saxlama IMAP-dan 20 MB
        // çəkərdi — istifadəçi yazdıqca sistem özünü yükləyərdi.
        ...(draft.forwardOf ? { forwardOf: draft.forwardOf } : {}),
        ...(files.length
          ? {
              attachments: files.map((f) => ({
                filename: f.filename,
                content: f.content,
                contentType: f.contentType,
              })),
            }
          : {}),
      });
      sent.current = true;
      // ⚠️ Qaralama SİLİNMƏLİDİR. Yoxsa göndərilmiş məktubun nüsxəsi
      // Qaralamalarda qalır və istifadəçi onu ikinci dəfə göndərir.
      if (draftUid !== null) {
        await api.deleteForever('Drafts', [draftUid]).catch(() => undefined);
      }
      step(`Göndərildi: ${r.accepted.length} qəbul, ${r.rejected.length} rədd`);
      onSent(r);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? messageFor(err.code, err.body)
          : messageFor('unknown'),
      );
      setBusy(false);
    }
  }

  return (
    <div className="compose-wrap" role="dialog" aria-label="Yeni məktub">
      <form className="compose" onSubmit={submit}>
        <header className="compose-bar">
          <span>{draft.inReplyTo ? 'Cavab' : 'Yeni məktub'}</span>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Bağla">
            <Icon name="close" size={17} />
          </button>
        </header>

        {/*
          «KİMDƏN» — LƏQƏB VARSA SEÇİM, YOXSA SADƏ MƏTN.
          ⚠️ Bir variant olanda açılan siyahı GÖSTƏRİLMİR. Tək seçimi olan
          `<select>` istifadəçini «burada nəsə seçmək lazımdır?» deyə
          düşündürür və heç bir fayda vermir — Gmail də eyni cür davranır.
        */}
        <div className="compose-from">
          <span className="compose-label">Kimdən</span>
          {identities.length > 1 ? (
            <select
              className="compose-fromsel"
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              aria-label="Göndərən ünvan"
            >
              {identities.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          ) : (
            <span className="compose-fromval">{from}</span>
          )}
        </div>

        <div className="compose-row">
          <span className="compose-label">Kimə</span>
          <input
            autoFocus={!draft.to}
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="ad@domen.az, ikinci@domen.az"
          />
          {!showCc ? (
            <button type="button" className="linkish" onClick={() => setShowCc(true)}>
              Kopya
            </button>
          ) : null}
        </div>

        {showCc ? (
          <>
            <div className="compose-row">
              <span className="compose-label">Kopya</span>
              <input value={cc} onChange={(e) => setCc(e.target.value)} />
            </div>
            <div className="compose-row">
              <span className="compose-label">Gizli</span>
              <input value={bcc} onChange={(e) => setBcc(e.target.value)} />
            </div>
          </>
        ) : null}

        <div className="compose-row">
          <span className="compose-label">Mövzu</span>
          <input
            autoFocus={Boolean(draft.to) && !draft.subject}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="(mövzusuz)"
          />
        </div>

        <textarea
          className="compose-body"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Məktubunuzu yazın…"
        />

        {error ? (
          <p className="login-error" role="alert">
            {error}
          </p>
        ) : null}

        {/*
          YÖNLƏNDİRİLƏN QOŞMALAR — GÖSTƏRİLİR, AMMA ÇIXARILA BİLMİR.
          ⚠️ Göstərmək məcburidir: istifadəçi hesab-fakturanı yönləndirib
          onun getdiyinə əmin olmalıdır. Əvvəl heç nə görünmürdü və
          məktub qoşmasız gedirdi.
          ⚠️ «×» düyməsi YOXDUR — fayllar brauzerdə deyil, serverdə IMAP-dan
          götürülür. Silmə düyməsi qoysaq, onu yalnız görüntüdən silərdi;
          məktub isə qoşma ilə gedərdi. İşləməyən düymə heç bir düymədən
          pisdir.
        */}
        {draft.forwardAttachments?.length ? (
          <div className="cmp-files">
            {draft.forwardAttachments.map((f, i) => (
              <span className="cmp-file is-fwd" key={`fwd-${i}`} title="Yönləndirilən qoşma">
                <Icon name="paperclip" size={14} />
                <span className="cmp-file-name">{f.filename}</span>
                <span className="cmp-file-size">{fmtSize(f.size)}</span>
              </span>
            ))}
          </div>
        ) : null}

        {files.length > 0 && (
          <div className="cmp-files">
            {files.map((f, i) => (
              <span className="cmp-file" key={i}>
                <Icon name="paperclip" size={14} />
                <span className="cmp-file-name">{f.filename}</span>
                <span className="cmp-file-size">{fmtSize(f.size)}</span>
                <button
                  type="button"
                  className="cmp-file-x"
                  onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                  aria-label={`${f.filename} — çıxar`}
                >
                  <Icon name="close" size={13} />
                </button>
              </span>
            ))}
            <span className="cmp-file-total">Cəmi {fmtSize(totalSize)}</span>
          </div>
        )}

        <footer className="compose-foot">
          <span className="cmp-saved">
            {savingDraft
              ? 'Saxlanılır…'
              : savedAt
                ? `Qaralama saxlanıldı ${timeOnly(savedAt)}`
                : ''}
          </span>
          <button type="submit" className="btn-primary" disabled={busy || attaching}>
            {busy ? 'Göndərilir…' : 'Göndər'}
          </button>
          {/* Gizli fayl seçici — düymə onu tetikləyir, çünki brauzerin
              standart `<input type=file>` görünüşü hər OS-də fərqlidir. */}
          <input
            ref={fileInput}
            type="file"
            multiple
            className="cmp-file-input"
            onChange={(e) => void addFiles(e.target.files)}
          />
          <button
            type="button"
            className="btn-text"
            onClick={() => fileInput.current?.click()}
            disabled={attaching}
          >
            <Icon name="paperclip" size={16} /> {attaching ? 'Əlavə edilir…' : 'Fayl əlavə et'}
          </button>
          <button
            type="button"
            className="btn-text"
            onClick={() => {
              // Bağlamazdan əvvəl saxlayırıq — 3 saniyəlik gecikməni
              // gözləmirik, çünki pəncərə dərhal yox olacaq.
              void saveDraft().finally(onClose);
            }}
          >
            Bağla
          </button>
        </footer>
      </form>
    </div>
  );
}
